import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import { useTranslation } from "../utils/i18n";

const SERVICE_ICONS = [
  <StorefrontOutlinedIcon sx={{ fontSize: 26, color: "#FF6A00" }} />,
  <EngineeringOutlinedIcon sx={{ fontSize: 26, color: "#FF6A00" }} />,
  <HeadsetMicOutlinedIcon sx={{ fontSize: 26, color: "#FF6A00" }} />,
];

const BRANDS = [{ name: "AUTEC", logo: "/images/partners/Autec_logo.svg" }];

function AboutUsComponent() {
  const { translations } = useTranslation();
  const ab = translations.aboutPage;
  const services = ab.whatWeDo.services;
  const stats = ab.stats;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1440,
        mx: "auto",
        px: { xs: 2, sm: 3, md: 5 },
        pt: { xs: 5, md: 8 },
        pb: { xs: 8, md: 12 },
      }}
    >
      {/* Who we are */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 380px" },
          gap: { xs: 4, md: 6 },
          alignItems: "start",
          mb: { xs: 8, md: 12 },
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: "#FF6A00",
              letterSpacing: 2,
              fontWeight: 700,
              fontSize: "0.72rem",
            }}
          >
            {ab.whoWeAre.overline}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.04em",
              color: "#0B0F13",
              mt: 0.75,
              mb: 3,
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              lineHeight: 1.15,
            }}
          >
            {ab.whoWeAre.title}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {[ab.whoWeAre.p1, ab.whoWeAre.p2, ab.whoWeAre.p3].map((p, i) => (
              <Typography
                key={i}
                sx={{ color: "#475467", lineHeight: 1.75, fontSize: "1rem" }}
              >
                {p}
              </Typography>
            ))}
          </Box>
        </Box>

        {/* Stats block */}
        <Box
          sx={{
            bgcolor: "#0B0F13",
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {stats.map((s) => (
            <Box key={s.label}>
              <Typography
                sx={{
                  fontSize: { xs: "2.5rem", md: "3rem" },
                  fontWeight: 800,
                  color: "#FF6A00",
                  letterSpacing: "-0.04em",
                  lineHeight: 1,
                  mb: 0.5,
                }}
              >
                {s.value}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
              >
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* What we do */}
      <Box sx={{ mb: { xs: 8, md: 12 } }}>
        <Typography
          variant="overline"
          sx={{
            color: "#FF6A00",
            letterSpacing: 2,
            fontWeight: 700,
            fontSize: "0.72rem",
          }}
        >
          {ab.whatWeDo.overline}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            letterSpacing: "-0.04em",
            color: "#0B0F13",
            mt: 0.75,
            mb: { xs: 3, md: 4 },
            fontSize: { xs: "1.75rem", md: "2.25rem" },
            lineHeight: 1.15,
          }}
        >
          {ab.whatWeDo.title}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 2.5,
          }}
        >
          {services.map((s, i) => (
            <Box
              key={s.title}
              sx={{
                bgcolor: "#fff",
                borderRadius: 3,
                border: "1px solid #E3E6EB",
                p: { xs: 3, md: 3.5 },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: "rgba(255,106,0,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2.5,
                }}
              >
                {SERVICE_ICONS[i]}
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#0B0F13",
                  mb: 1,
                  fontSize: "1.02rem",
                }}
              >
                {s.title}
              </Typography>
              <Typography
                sx={{ color: "#475467", lineHeight: 1.7, fontSize: "0.92rem" }}
              >
                {s.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Our brands */}
      <Box>
        <Typography
          variant="overline"
          sx={{
            color: "#FF6A00",
            letterSpacing: 2,
            fontWeight: 700,
            fontSize: "0.72rem",
          }}
        >
          {ab.brands.overline}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            letterSpacing: "-0.04em",
            color: "#0B0F13",
            mt: 0.75,
            mb: { xs: 3, md: 4 },
            fontSize: { xs: "1.75rem", md: "2.25rem" },
            lineHeight: 1.15,
          }}
        >
          {ab.brands.title}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 2,
          }}
        >
          {BRANDS.map((b) => (
            <Box
              key={b.name}
              sx={{
                bgcolor: "#fff",
                border: "1px solid #E3E6EB",
                borderRadius: 3,
                p: { xs: 3, md: 4 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 100,
              }}
            >
              {b.logo ? (
                <Box
                  component="img"
                  src={b.logo}
                  alt={b.name}
                  sx={{ height: { xs: 32, md: 38 }, objectFit: "contain" }}
                />
              ) : (
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                    color: "#0B0F13",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {b.name}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default AboutUsComponent;
